import { useMemo, useState } from "react";
import { AlertTriangle, Link2 } from "lucide-react";
import { getRealizadoNfs } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import SincronizacaoOmie from "../../components/gestaoRealizado/SincronizacaoOmie";
import { ModalVinculoNf } from "../../components/gestaoRealizado/VinculoNf";
import {
  ANO_ATUAL, AvisoErro, Badge, BarraFiltros, Card, Carregando, GradeKpis, Kpi, Seletor, Vazio, estiloTabela,
  botaoPequeno, formatCompacto, formatCompetencia, formatData, formatValor, opcoesAno, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Caixa: NF | emissão | valor | pago/aberto | recebimento | GP | projeto. Valor, retenções e
// recebimento vêm do OMIE (botão Sincronizar) ou, no histórico, da aba Nfe oficial; sem esses
// dados, vale a soma das baixas de competência ou o valor da solicitação que citou a NF.
// "Vínculo" ajusta à mão a solicitação da NF (nota reemitida/excluída/casada errado no OMIE).

const PENDENCIAS = {
  NF_SEM_SOLICITACAO: "Sem solicitação",
  NF_CASAMENTO_AMBIGUO: "Vínculo a conferir",
  NF_VALOR_DIVERGENTE: "Valor diferente da solicitação",
  NF_EXCLUIDA_OMIE: "Excluída no OMIE",
  NF_NAO_ENCONTRADA_OMIE: "Não está no OMIE",
};

export default function NotasFiscaisRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const [ano, setAno] = useState(ANO_ATUAL);
  const [status, setStatus] = useState("");
  const [busca, setBusca] = useState("");
  const [vinculoNf, setVinculoNf] = useState(null);
  const [soPendencias, setSoPendencias] = useState(false);
  const { dados, carregando, erro, recarregar } = useCarregar(() => getRealizadoNfs(ano, status), [ano, status]);
  const T = estiloTabela(C);

  const nfs = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (dados || [])
      .filter((n) => !soPendencias || n.pendencias.length)
      .filter((n) => !termo || n.numero.includes(termo) || n.projetos.join(" ").toLowerCase().includes(termo) || n.gps.join(" ").toLowerCase().includes(termo));
  }, [dados, busca, soPendencias]);
  const total = (lista) => lista.reduce((a, n) => a + (n.valor || 0), 0);
  const pagas = nfs.filter((n) => n.status === "PAGO");
  const abertas = nfs.filter((n) => n.status === "ABERTO");
  const agings = pagas.map((n) => n.agingFaturamentoRecebimento).filter((v) => v !== null);

  return (
    <RealizadoLayout titulo="Notas fiscais" isLight={isLight} setIsLight={setIsLight}>
      <BarraFiltros>
        <Seletor C={C} rotulo="Ano da emissão" value={ano} onChange={(v) => setAno(Number(v))} opcoes={opcoesAno} />
        <Seletor C={C} rotulo="Status" value={status} onChange={setStatus} opcoes={[
          { value: "", label: "Todos" }, { value: "ABERTO", label: "Em aberto" }, { value: "PAGO", label: "Pagas" }, { value: "CANCELADO", label: "Canceladas" },
        ]} />
        <Seletor C={C} rotulo="Divergência com o OMIE" value={soPendencias ? "sim" : ""} onChange={(v) => setSoPendencias(v === "sim")} opcoes={[
          { value: "", label: "Todas as notas" }, { value: "sim", label: "Só com pendência" },
        ]} />
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Buscar
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nº, projeto ou GP" style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: C.text, minHeight: 36, boxSizing: "border-box" }} />
        </label>
      </BarraFiltros>
      <SincronizacaoOmie C={C} onConcluido={recarregar} />
      <AvisoErro C={C}>{erro}</AvisoErro>

      {carregando && !dados ? <Carregando C={C} /> : (
        <>
          <GradeKpis>
            <Kpi C={C} rotulo="Notas no período" valor={nfs.length} detalhe={formatCompacto(total(nfs))} />
            <Kpi C={C} rotulo="Recebidas" valor={pagas.length} detalhe={formatCompacto(total(pagas))} cor={C.success} />
            <Kpi C={C} rotulo="Em aberto" valor={abertas.length} detalhe={formatCompacto(total(abertas))} cor={C.warning} />
            <Kpi C={C} rotulo="Prazo médio de recebimento" valor={agings.length ? `${Math.round(agings.reduce((a, b) => a + b, 0) / agings.length)} dias` : "—"} detalhe="Emissão → recebimento" />
          </GradeKpis>
          <Card C={C} titulo={`${nfs.length} nota(s) fiscal(is)`} subtitulo="Fonte: OMIE quando sincronizado; histórico da aba Nfe oficial">
            {nfs.length === 0 ? <Vazio C={C}>Nenhuma nota no período.</Vazio> : (
              <div style={{ ...T.wrap, maxHeight: "68vh" }}>
                <table style={{ ...T.table, minWidth: 1150 }}>
                  <thead>
                    <tr>
                      <th style={T.th}>NF</th>
                      <th style={T.th}>Empresa</th>
                      <th style={T.th}>Emissão</th>
                      <th style={{ ...T.th, ...T.thNum }}>Valor bruto</th>
                      <th style={{ ...T.th, ...T.thNum }}>Retenções</th>
                      <th style={{ ...T.th, ...T.thNum }}>Líquido</th>
                      <th style={T.th}>Status</th>
                      <th style={T.th}>Recebimento</th>
                      <th style={{ ...T.th, ...T.thNum }}>Dias</th>
                      <th style={T.th}>Projeto(s)</th>
                      <th style={T.th}>GP</th>
                      <th style={T.th}>Competência(s)</th>
                      <th style={T.th}>Solicitação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nfs.map((n) => (
                      <tr key={n.id}>
                        <td style={{ ...T.td, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {n.numero}
                          {n.pendencias.length > 0 && (
                            <span title={n.pendencias.map((p) => PENDENCIAS[p] || p).join(" · ")} style={{ color: C.warning, marginLeft: 6, verticalAlign: "middle" }}>
                              <AlertTriangle size={13} />
                            </span>
                          )}
                        </td>
                        <td style={T.td}>{n.empresa || "—"}</td>
                        <td style={T.td}>{formatData(n.dataEmissao)}</td>
                        <td style={{ ...T.td, ...T.tdNum }} title={n.valorOrigem === "nota" ? `Fonte: ${n.origemDados === "OMIE" ? "OMIE" : "Nfe oficial"}` : "Sem dados da nota — soma das baixas/solicitação"}>
                          {formatValor(n.valor)}{n.valorOrigem !== "nota" ? "*" : ""}
                        </td>
                        <td style={{ ...T.td, ...T.tdNum, color: C.muted }}>{formatValor(n.retencoes)}</td>
                        <td style={{ ...T.td, ...T.tdNum }}>{formatValor(n.valorLiquido)}</td>
                        <td style={T.td}>
                          <Badge cor={n.status === "PAGO" ? C.success : n.status === "CANCELADO" ? C.danger : C.warning}>{n.status === "PAGO" ? "Paga" : n.status === "CANCELADO" ? "Cancelada" : "Em aberto"}</Badge>
                        </td>
                        <td style={T.td}>{n.dataRecebimento ? formatData(n.dataRecebimento) : n.expectativaRecebimento ? <span style={{ color: C.muted }}>prev. {formatData(n.expectativaRecebimento)}</span> : "—"}</td>
                        <td style={{ ...T.td, ...T.tdNum }}>{n.agingFaturamentoRecebimento ?? "—"}</td>
                        <td style={{ ...T.td, maxWidth: 240 }}>{n.projetos.join(", ") || "—"}</td>
                        <td style={T.td}>{n.gps.join(", ") || "—"}</td>
                        <td style={T.td}>{n.competencias.map(formatCompetencia).join(", ") || "—"}</td>
                        <td style={{ ...T.td, whiteSpace: "nowrap" }}>
                          <button
                            style={botaoPequeno(n.solicitacaoId ? C.brand : C.warning, true)}
                            onClick={() => setVinculoNf(n.id)}
                            title={n.vinculoManualPor ? `Ajustado à mão por ${n.vinculoManualPor}` : "Ver ou ajustar a solicitação desta nota"}
                          >
                            <Link2 size={13} /> {n.solicitacaoId ? `${n.solicitacaoId}${n.vinculoManualPor ? " · manual" : ""}` : "Vincular"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
              * sem dados da nota (OMIE / Nfe oficial): soma das baixas de competência ou valor da solicitação.
              ⚠ = divergência com o OMIE no Log de erros (passe o mouse para ver).
            </div>
          </Card>
          {vinculoNf && <ModalVinculoNf C={C} nfId={vinculoNf} onClose={() => setVinculoNf(null)} onAtualizado={recarregar} />}
        </>
      )}
    </RealizadoLayout>
  );
}
